/** Integration/backend terms that must never appear in customer-facing product copy. */
const INTEGRATION_LEAK_PATTERN =
  /\b(?:ship\s*station|shipstation|wrangler|cloudflare\s*d1|\bd1\b)\b/i;

const SHIPSTATION_CATALOG_VARIANTS_PATTERN =
  /with\s+(\d+)\s+ship\s*station\s+catalog\s+variants?\.?/gi;

const SHIPSTATION_CATALOG_PATTERN = /ship\s*station\s+catalog\s+variants?/gi;

export interface CustomerProductDescriptionOptions {
  /** Number of purchasable variants (sizes/options). */
  variantCount?: number;
  /** When true, notes that full marketing copy is pending admin review. */
  pendingReview?: boolean;
}

/**
 * Builds default product description text safe for storefront display.
 * Never mentions backend integrations or catalog systems.
 */
export function buildCustomerProductDescription(
  displayName: string,
  options?: CustomerProductDescriptionOptions
): string {
  const trimmedName = displayName.trim();
  const variantCount = options?.variantCount;

  if (typeof variantCount === 'number' && variantCount > 1) {
    const sizeLabel = variantCount === 1 ? 'size' : 'sizes';
    return `${trimmedName} — research-grade compound available in ${variantCount} ${sizeLabel}.`;
  }

  if (options?.pendingReview) {
    return `${trimmedName} — research-grade compound. Description pending admin review.`;
  }

  return `${trimmedName} — research-grade compound for laboratory research.`;
}

/**
 * Returns true when text contains known integration/backend leaks.
 */
export function containsIntegrationLeak(text: string): boolean {
  if (!text.trim()) {
    return false;
  }
  return INTEGRATION_LEAK_PATTERN.test(text) || SHIPSTATION_CATALOG_PATTERN.test(text);
}

/**
 * Strips or rewrites integration references from customer-visible strings.
 * Idempotent — safe to run on already-sanitized copy.
 */
export function sanitizeCustomerFacingCopy(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) {
    return trimmed;
  }

  let result = trimmed;

  result = result.replace(SHIPSTATION_CATALOG_VARIANTS_PATTERN, (_match, count: string) => {
    const n = Number.parseInt(count, 10);
    const sizeLabel = n === 1 ? 'size' : 'sizes';
    return `available in ${n} ${sizeLabel}.`;
  });

  result = result.replace(SHIPSTATION_CATALOG_PATTERN, 'size options');
  result = result.replace(/\bship\s*station\b/gi, '');
  result = result.replace(/\bwrangler\b/gi, '');
  result = result.replace(/\bcloudflare\s*d1\b/gi, '');
  result = result.replace(/\bd1\s+database\b/gi, '');

  result = result
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+([.,;:!?])/g, '$1')
    .replace(/\s+—\s+—\s+/g, ' — ')
    .replace(/—\s+\./g, '.')
    .trim();

  return result;
}
